import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from "@mui/material";
import { Delete, CloudUpload } from "@mui/icons-material";
import { toast } from "react-toastify";
import useApi from "../useApi";

const DatasetComponent = () => {
    const [{ data: quotes, loading }, callApi] = useApi();
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const [file, setFile] = useState(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        open: false,
        quoteId: null,
    });

    useEffect(() => {
        callApi({
            url: `/api/qp/quotes`,
        });
    }, [callApi]);

    const handleUploadQuote = (e) => {
        e.preventDefault();
        if (!file) {
            toast.error("Please select a file to upload.");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        callApi({
            url: `/api/qp/quotes`,
            method: "POST",
            body: formData,
        })
            .then(() => {
                toast.success("Quote uploaded successfully!");
                setFile(null);
                setOpenUploadModal(false);
                callApi({
                    url: `/api/qp/quotes`,
                });
            })
            .catch(() => {
                toast.error("Failed to upload quote.");
            });
    };

    const handleDeleteQuote = (quoteId) => {
        callApi({
            url: `/api/qp/quotes/${quoteId}`,
            method: "DELETE",
        })
            .then(() => {
                toast.success("Quote deleted successfully!");
                callApi({
                    url: `/api/qp/quotes`,
                });
            })
            .catch(() => {
                toast.error("Failed to delete quote.");
            });
        setDeleteConfirmation({ open: false, quoteId: null });
    };

    const getFileTypeStats = () => {
        if (!quotes || !Array.isArray(quotes)) return {};
        return quotes.reduce((acc, quote) => {
            const fileName = quote.file_name || "";
            const fileExtension = getFileType(fileName);
            acc[fileExtension] = (acc[fileExtension] || 0) + 1;
            return acc;
        }, {});
    };

    const getFileType = (fileName) => {
        return fileName.split(".").pop().toLowerCase() || "Unknown";
    };

    const fileTypeStats = getFileTypeStats();

    return (
        <Box sx={{ padding: 3 }}>
            <Typography variant="h4" gutterBottom>
                Quote Dataset
            </Typography>

            {loading ? (
                <CircularProgress />
            ) : (
                <>
                    <Box sx={{ marginBottom: 2 }}>
                        <Typography variant="h6">Statistics</Typography>
                        <Typography>
                            Total Quotes: {quotes ? quotes.length : 0}
                        </Typography>
                        {Object.entries(fileTypeStats).map(
                            ([fileType, count]) => (
                                <Typography key={fileType}>
                                    {fileType}: {count}
                                </Typography>
                            ),
                        )}
                    </Box>

                    <Button
                        variant="contained"
                        startIcon={<CloudUpload />}
                        onClick={() => setOpenUploadModal(true)}
                        sx={{ marginBottom: 2 }}
                    >
                        Upload Quote
                    </Button>

                    <List>
                        {quotes &&
                            quotes.map((quote) => (
                                <ListItem key={quote.id}>
                                    <ListItemText
                                        primary={
                                            quote.file_name.length > 50
                                                ? quote.file_name.substring(
                                                      0,
                                                      50,
                                                  ) + "..."
                                                : quote.file_name
                                        }
                                        secondary={`File Type: ${getFileType(quote.file_name)}`}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            onClick={() =>
                                                setDeleteConfirmation({
                                                    open: true,
                                                    quoteId: quote.id,
                                                })
                                            }
                                        >
                                            <Delete />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                    </List>

                    <Dialog
                        open={openUploadModal}
                        onClose={() => setOpenUploadModal(false)}
                    >
                        <DialogTitle>Upload Quote</DialogTitle>
                        <DialogContent>
                            <input
                                type="file"
                                onChange={(e) => setFile(e.target.files[0])}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenUploadModal(false)}>
                                Cancel
                            </Button>
                            <Button onClick={handleUploadQuote}>Upload</Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={deleteConfirmation.open}
                        onClose={() =>
                            setDeleteConfirmation({
                                open: false,
                                quoteId: null,
                            })
                        }
                    >
                        <DialogTitle>Confirm Deletion</DialogTitle>
                        <DialogContent>
                            Are you sure you want to delete this quote?
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() =>
                                    setDeleteConfirmation({
                                        open: false,
                                        quoteId: null,
                                    })
                                }
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() =>
                                    handleDeleteQuote(
                                        deleteConfirmation.quoteId,
                                    )
                                }
                                color="error"
                            >
                                Delete
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </Box>
    );
};

export default DatasetComponent;
